/**
 * SEO Score Calculator
 * Mirrors the Flutter SeoScoreCalculator for consistent scoring across platforms
 */

/**
 * Calculate SEO Content Analysis score from "Overall SEO score" field
 */
export const calculateSeoContentAnalysisScore = (data) => {
  if (!data || typeof data !== 'object') return 0;
  
  console.log('[SEO Score] Content Analysis data:', data);
  
  // Look for score in Overview
  const overview = data.Overview;
  if (overview && typeof overview === 'object') {
    const score = overview['Overall SEO score'];
    if (score !== null && score !== undefined) {
      const scoreVal = typeof score === 'number' ? Math.floor(score) : parseInt(score, 10) || 0;
      console.log('✅ Found Overall SEO score in Overview:', scoreVal);
      return Math.max(0, Math.min(100, scoreVal));
    }
  }
  
  // Fallback: Try top-level
  const score = data['Overall SEO score'];
  if (score !== null && score !== undefined) {
    const scoreVal = typeof score === 'number' ? Math.floor(score) : parseInt(score, 10) || 0;
    return Math.max(0, Math.min(100, scoreVal));
  }
  
  console.log('❌ No Overall SEO score found');
  return 0;
};

/**
 * Calculate Readability Score from Flesch-Kincaid Reading Ease metric
 */
export const calculateReadabilityScore = (data) => {
  if (!data || typeof data !== 'object') return 0;

  const stats = (data.data && typeof data.data === 'object') ? data.data : data;
  if (!stats || typeof stats !== 'object') return 0;

  const flesch = stats['Flesch Kincaid Reading Ease'];
  if (!flesch || typeof flesch !== 'object') return 0;
  
  const score = flesch.score;
  if (score !== null && score !== undefined) {
    const scoreVal = typeof score === 'number' ? Math.floor(score) : parseInt(score, 10) || 0;
    return Math.max(0, Math.min(100, scoreVal));
  }
  return 0;
};

/**
 * Calculate Canonical tag score
 * Scoring: 0=not found, 20=found but empty, 60=differs from input, 100=matches input
 */
export const calculateCanonicalScore = (data) => {
  let dataList = [];
  
  if (Array.isArray(data)) {
    dataList = data;
  } else if (data && typeof data === 'object' && Array.isArray(data.data)) {
    dataList = data.data;
  } else if (data && typeof data === 'object') {
    dataList = [data];
  } else {
    return 0;
  }
  
  if (dataList.length === 0) return 0;
  
  const first = dataList[0];
  if (!first || typeof first !== 'object') return 0;
  
  const found = (first['Canonical found'] || '').toString().toLowerCase();
  const inputUrl = first['Input URL'] || '';
  const canonicalUrl = first.Canonical || '';
  
  if (!found || found === 'not found') return 0;
  if (!canonicalUrl) return 20;
  if (inputUrl === canonicalUrl) return 100;
  return 60;
};

/**
 * Calculate Image Analysis score: (SEO Score / Max SEO score available) * 100
 */
export const calculateImageAnalysisScore = (data) => {
  console.log('[SEO Score] Image Analysis data type:', typeof data);
  
  let targetMap = {};
  
  if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
    targetMap = data[0];
    console.log('   Extracted from array[0]');
  } else if (data && typeof data === 'object') {
    if (Array.isArray(data.data) && data.data.length > 0) {
      const firstItem = data.data[0];
      if (typeof firstItem === 'object') {
        targetMap = firstItem;
        console.log('   Extracted from data[0]');
      } else {
        targetMap = data;
      }
    } else {
      targetMap = data;
    }
  } else {
    return 0;
  }
  
  const seoScore = targetMap['SEO Score'];
  const maxScore = targetMap['Max SEO score available'];
  
  console.log('   SEO Score:', seoScore, 'Max:', maxScore);
  
  if (seoScore === null || seoScore === undefined || maxScore === null || maxScore === undefined) {
    console.log('❌ Missing SEO Score or Max Score');
    return 0;
  }
  
  const seoVal = typeof seoScore === 'number' ? Math.floor(seoScore) : parseInt(seoScore, 10) || 0;
  const maxVal = typeof maxScore === 'number' ? Math.floor(maxScore) : parseInt(maxScore, 10) || 1;
  
  if (maxVal > 0) {
    const percentage = Math.round((seoVal / maxVal) * 100);
    console.log('✅ Image Analysis score:', percentage);
    return Math.max(0, Math.min(100, percentage));
  }
  return 0;
};

/**
 * Calculate Headings score based on h1-h6 structure
 */
export const calculateHeadingsScore = (data) => {
  if (!data || typeof data !== 'object') return 0;
  
  console.log('[SEO Score] Headings data keys:', Object.keys(data));
  
  let headingMap = { ...data };
  if (data.data && typeof data.data === 'object') {
    headingMap = { ...data.data };
    console.log('📊 Found inner data object for headings');
  }
  
  let score = 50; // Start with base score
  
  const h1 = headingMap.h1;
  const h1Count = Array.isArray(h1) ? h1.length : 0;
  
  console.log('   h1 count:', h1Count);
  
  // H1 scoring
  if (h1Count === 0) {
    score -= 30;
  } else if (h1Count === 1) {
    score += 10;
  } else {
    score -= 20;
  }
  
  let total = 0;
  for (const tag of ['h1', 'h2', 'h3', 'h4', 'h5', 'h6']) {
    const tagList = headingMap[tag];
    if (Array.isArray(tagList)) total += tagList.length;
  }
  
  console.log('   total headings:', total);
  
  if (total >= 3 && total <= 15) {
    score += 20;
  } else if (total > 15) {
    score -= 10;
  }
  
  const finalScore = Math.max(0, Math.min(100, score));
  console.log('✅ Headings score:', finalScore);
  return finalScore;
};

/**
 * Calculate Helpful Content score
 * Formula: (main 50% + category average 30% + earned/available ratio 20%)
 */
export const calculateHelpfulContentScore = (data) => {
  if (!data || typeof data !== 'object') return 0;

  try {
    const root = (data.data && typeof data.data === 'object') ? data.data : data;

    const collectRatings = (obj) => {
      const ratings = [];
      if (!obj || typeof obj !== 'object') return ratings;

      Object.values(obj).forEach((val) => {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
          Object.values(val).forEach((subVal) => {
            let parsed = subVal;
            if (typeof subVal === 'string') {
              try {
                parsed = JSON.parse(subVal);
              } catch {
                parsed = subVal;
              }
            }

            if (parsed && typeof parsed === 'object' && parsed.rating !== undefined) {
              const ratingNum = typeof parsed.rating === 'number'
                ? parsed.rating
                : parseFloat(parsed.rating) || 0;
              if (ratingNum > 0) {
                ratings.push(ratingNum);
              }
            }
          });
        }
      });

      return ratings;
    };

    const earned = root['earned points'] ?? data['earned points'] ?? 0;
    const available = root['available points'] ?? data['available points'] ?? 0;

    const earnedNum = typeof earned === 'number' ? earned : parseFloat(earned) || 0;
    const availableNum = typeof available === 'number' ? available : parseFloat(available) || 0;

    if (availableNum === 0) {
      const ratings = collectRatings(root);
      if (ratings.length > 0) {
        const avgRating = ratings.reduce((a, b) => a + b, 0) / ratings.length;
        return Math.max(0, Math.min(100, Math.round((avgRating / 10) * 100)));
      }
      return 0;
    }

    const mainScore = (earnedNum / availableNum) * 100;
    const weightedMain = mainScore * 0.5;

    // Category average (30%)
    const categoryScore = root['category score'] || data['category score'] || {};
    let catTotal = 0;
    let count = 0;

    Object.values(categoryScore).forEach((value) => {
      if (value && typeof value === 'object') {
        const cEarned = value['earned points'] ?? 0;
        const cAvailable = value['available points'] ?? 0;

        const cEarnedNum = typeof cEarned === 'number' ? cEarned : parseFloat(cEarned) || 0;
        const cAvailableNum = typeof cAvailable === 'number' ? cAvailable : parseFloat(cAvailable) || 0;

        if (cAvailableNum > 0) {
          catTotal += (cEarnedNum / cAvailableNum) * 100;
          count++;
        }
      }
    });

    const avgCategory = count > 0 ? catTotal / count : 0;
    const weightedCategory = avgCategory * 0.3;

    // Earned / Available bonus (20%)
    const weightedEarned = mainScore * 0.2;

    const finalScore = Math.round(weightedMain + weightedCategory + weightedEarned);

    return Math.max(0, Math.min(100, finalScore));
  } catch (e) {
    console.error('Error calculating helpful content score:', e);
    return 0;
  }
};

/**
 * Calculate On Page Links (internal-links) score from SEO Score field
 */
export const calculateOnPageLinksScore = (data) => {
  if (!data || typeof data !== 'object') return 0;

  let targetMap = null;

  if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
    targetMap = data[0];
  } else if (data && typeof data === 'object') {
    if (Array.isArray(data.data) && data.data.length > 0) {
      targetMap = data.data[0];
    } else if (data.data && Array.isArray(data.data.data) && data.data.data.length > 0) {
      targetMap = data.data.data[0];
    } else if (data.data && typeof data.data === 'object') {
      targetMap = data.data;
    } else {
      targetMap = data;
    }
  }

  if (!targetMap || typeof targetMap !== 'object') return 0;

  const earned = targetMap['SEO Score'];
  const max = targetMap['Max SEO score available'];

  const earnedNum = typeof earned === 'number' ? earned : parseFloat(earned) || 0;
  const maxNum = typeof max === 'number' ? max : parseFloat(max) || 0;

  if (maxNum === 0) return 0;

  return Math.max(0, Math.min(100, Math.round((earnedNum / maxNum) * 100)));
};

/**
 * Calculate Keyword Suggestions score (Count-based)
 */
export const calculateKeywordSuggestionsScore = (data) => {
  try {
    console.log('[SEO Score] Keyword Suggestions data type:', typeof data);
    if (data && typeof data === 'object') {
      console.log('   keys:', Object.keys(data));
    }
    
    let suggestions = [];
    
    if (Array.isArray(data)) {
      suggestions = data;
      console.log('   Direct array:', suggestions.length, 'items');
    } else if (data && typeof data === 'object') {
      console.log('   Checking for suggestions/data keys...');
      
      if (Array.isArray(data.data)) {
        suggestions = data.data;
        console.log('   Found in data:', suggestions.length, 'items');
      } else if (Array.isArray(data.suggestions)) {
        suggestions = data.suggestions;
        console.log('   Found in suggestions:', suggestions.length, 'items');
      }
    }
    
    const count = suggestions.length;
    console.log('   Total suggestions count:', count);
    
    if (count === 0) {
      console.log('✅ No suggestions = 100% (fully optimized)');
      return 100;
    }
    if (count <= 5) {
      console.log('✅ 1-5 suggestions = 75%');
      return 75;
    }
    if (count <= 15) {
      console.log('✅ 6-15 suggestions = 50%');
      return 50;
    }
    if (count <= 50) {
      console.log('✅ 16-50 suggestions = 30%');
      return 30;
    }
    console.log('✅ 50+ suggestions = 10%');
    return 10;
  } catch (e) {
    console.error('Error in keyword suggestions:', e);
    return 0;
  }
};

/**
 * Calculate Keyword Density score
 */
export const calculateKeywordDensityScore = (data) => {
  try {
    console.log('[Keyword Density] Raw input data:', data);
    
    let keywords = [];
    
    // Handle different data structures
    if (Array.isArray(data)) {
      // Direct array
      keywords = data;
      console.log('[Keyword Density] Detected direct array:', keywords.length, 'items');
    } else if (data && typeof data === 'object') {
      // Check for .data property (wrapped structure)
      if (Array.isArray(data.data)) {
        keywords = data.data;
        console.log('[Keyword Density] Found array in .data property:', keywords.length, 'items');
      } 
      // Check if it's a single object with keyword density info
      else if (data['Keyword density'] !== undefined || data['keyword density'] !== undefined || data.density !== undefined) {
        // Treat single object as array with one item
        keywords = [data];
        console.log('[Keyword Density] Detected single keyword density object');
      }
      // Check if data.data is an object (not array) that contains keyword info
      else if (data.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
        keywords = [data.data];
        console.log('[Keyword Density] Wrapped single object from .data');
      }
      else {
        console.warn('[Keyword Density] ❌ Invalid data structure:', Object.keys(data));
        return 0;
      }
    } else {
      console.warn('[Keyword Density] ❌ Data is neither array nor object:', typeof data);
      return 0;
    }
    
    if (keywords.length === 0) {
      console.warn('[Keyword Density] ❌ Empty keywords array');
      return 0;
    }

    let totalDensity = 0;
    let validCount = 0;
    const keywordTypes = new Set();
    let hasHighDensity = false;
    let hasKeyword = false;

    for (const k of keywords) {
      if (k && typeof k === 'object') {
        const rawDensity = k['Keyword density'] ?? k['keyword density'] ?? k.density;
        const type = (k['Keyword type'] ?? '').toString().toLowerCase();

        console.log('[Keyword Density] Processing keyword:', { rawDensity, type });

        if (type) {
          keywordTypes.add(type);
        }

        let density = 0;
        if (typeof rawDensity === 'string') {
          density = parseFloat(rawDensity.replace('%', '')) || 0;
        } else if (typeof rawDensity === 'number') {
          density = rawDensity;
        }

        console.log('[Keyword Density] Parsed density:', density);

        if (density > 0) {
          hasKeyword = true;
          validCount++;
          totalDensity += density;
        }

        if (density > 4) {
          hasHighDensity = true;
        }
      }
    }

    console.log('[Keyword Density] Summary:', { hasKeyword, validCount, totalDensity, keywordTypes: Array.from(keywordTypes) });

    if (!hasKeyword || validCount === 0) {
      console.warn('[Keyword Density] ❌ No valid keywords found');
      return 0;
    }

    const avgDensity = totalDensity / validCount;
    console.log('[Keyword Density] Avg density:', avgDensity);

    // Density Quality (70%)
    let densityScore;
    if (avgDensity >= 0.5 && avgDensity <= 2.5) {
      densityScore = 100;
      console.log('[Keyword Density] 0.5-2.5% range: 100');
    } else if (avgDensity >= 0.3 && avgDensity < 0.5) {
      densityScore = 80;
      console.log('[Keyword Density] 0.3-0.5% range: 80');
    } else if (avgDensity > 2.5 && avgDensity <= 3.5) {
      densityScore = 70;
      console.log('[Keyword Density] 2.5-3.5% range: 70');
    } else if (avgDensity >= 0.1) {
      densityScore = 50;
      console.log('[Keyword Density] 0.1+ range: 50');
    } else {
      densityScore = 0;
      console.log('[Keyword Density] < 0.1 range: 0');
    }

    // Coverage / Distribution (20%)
    let coverageScore;
    if (keywordTypes.size >= 3) {
      coverageScore = 100;
      console.log('[Keyword Density] 3+ types: 100');
    } else if (keywordTypes.size === 2) {
      coverageScore = 70;
      console.log('[Keyword Density] 2 types: 70');
    } else if (keywordTypes.size === 1) {
      coverageScore = 40;
      console.log('[Keyword Density] 1 type: 40');
    } else {
      coverageScore = 0;
      console.log('[Keyword Density] 0 types: 0');
    }

    // Calculate score
    let score = (densityScore * 0.7) + (coverageScore * 0.2);
    console.log('[Keyword Density] Base score:', score);

    // Penalties (10%)
    if (hasHighDensity) {
      score -= 20;
      console.log('[Keyword Density] High density penalty -20');
    }
    if (avgDensity < 0.2) {
      score -= 10;
      console.log('[Keyword Density] Low density penalty -10');
    }

    const finalScore = Math.max(0, Math.min(100, Math.round(score)));
    console.log('[Keyword Density] ✅ Final score:', finalScore);
    return finalScore;
  } catch (e) {
    console.error('Error in keyword density:', e);
    return 0;
  }
};

/**
 * Calculate PWA Audit score
 */
export const calculatePwaAuditScore = (data) => {
  try {
    console.log('[SEO Score] PWA Audit data type:', typeof data);
    
    let targetMap = {};
    
    if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
      targetMap = data[0];
      console.log('   Extracted from array[0]');
    } else if (data && typeof data === 'object') {
      if (Array.isArray(data.data) && data.data.length > 0) {
        const firstItem = data.data[0];
        if (typeof firstItem === 'object') {
          targetMap = firstItem;
          console.log('   Extracted from data[0]');
        } else {
          targetMap = data;
        }
      } else {
        targetMap = data;
      }
    } else {
      return 0;
    }

    // First, try to use Google PWA Score if available and non-zero
    let score = targetMap['Google PWA Score'];
    
    if (typeof score === 'number' && score > 0) {
      const result = Math.max(0, Math.min(100, Math.round(score)));
      console.log('✅ PWA Audit score from Google PWA Score:', result);
      return result;
    }
    
    // Fallback: Calculate from optimized vs improvements ratio
    const optimized = parseInt(targetMap['Optimized'] || 0);
    const improvements = parseInt(targetMap['Improvements'] || 0);
    const issuesCount = parseInt(targetMap['Issues to Fix'] || improvements);
    
    if (optimized > 0 && (optimized + issuesCount) > 0) {
      score = Math.round((optimized / (optimized + issuesCount)) * 100);
      console.log('✅ PWA Audit score from optimized ratio:', score, `(${optimized}/${optimized + issuesCount})`);
      return Math.max(0, Math.min(100, score));
    }
    
    console.log('❌ No PWA score calculation possible');
    return 0;
  } catch (e) {
    console.error('Error in PWA Audit:', e);
    return 0;
  }
};

/**
 * Calculate Title Tag score (SEO Score / Max SEO score available) * 100
 */
export const calculateTitleTagScore = (data) => {
  try {
    let targetMap = {};
    
    if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
      targetMap = { ...data[0] };
    } else if (data && typeof data === 'object') {
      targetMap = { ...data };
    } else {
      return 0;
    }
  
    const seoScore = targetMap['SEO Score'];
    const maxScore = targetMap['Max SEO score available'];
    
    if (typeof seoScore === 'number' && typeof maxScore === 'number' && maxScore > 0) {
      const percentage = Math.round((seoScore / maxScore) * 100);
      return Math.max(0, Math.min(100, percentage));
    }

    // Fallback logic
    let score = 50;

    const titleText = (targetMap['Title tag'] || '').toString().trim();
    if (titleText) score += 20;

    const length = titleText.length;
    if (length >= 30 && length <= 70) {
      score += 25;
    } else if (length > 0) {
      score += 10;
    }

    const feedback = targetMap['Feedback details'];
    if (feedback && typeof feedback === 'object') {
      if (Object.keys(feedback).length === 0) {
        score += 15;
      } else if (Object.keys(feedback).length <= 2) {
        score += 5;
      }
    }

    return Math.max(0, Math.min(100, score));
  } catch (e) {
    console.error('Error in title tag calculation:', e);
    return 0;
  }
};

/**
 * Calculate Meta Description score: (SEO Score / Max SEO score available) * 100
 */
export const calculateMetaDescriptionScore = (data) => {
  try {
    let targetMap = {};
    
    if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
      targetMap = data[0];
    } else if (data && typeof data === 'object') {
      targetMap = data;
    } else {
      return 0;
    }
  
    const seoScore = targetMap['SEO Score'];
    const maxScore = targetMap['Max SEO score available'];
    
    if (seoScore === null || seoScore === undefined || maxScore === null || maxScore === undefined) {
      return 0;
    }
    
    const seoVal = typeof seoScore === 'number' ? seoScore : parseFloat(seoScore) || 0;
    const maxVal = typeof maxScore === 'number' ? maxScore : parseFloat(maxScore) || 1;
    
    if (maxVal > 0) {
      const percentage = Math.round((seoVal / maxVal) * 100);
      return Math.max(0, Math.min(100, percentage));
    }
    return 0;
  } catch (e) {
    console.error('Error in meta description:', e);
    return 0;
  }
};

/**
 * Calculate Word Count score from SEO Score field
 */
export const calculateWordCountScore = (data) => {
  try {
    let targetMap = {};
    
    if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
      targetMap = data[0];
    } else if (data && typeof data === 'object') {
      targetMap = data;
    } else {
      return 0;
    }

    const seoScore = targetMap['SEO Score'];
    const maxScore = targetMap['Max SEO score available'];

    if (seoScore === null || seoScore === undefined || maxScore === null || maxScore === undefined) {
      return 0;
    }

    const earned = typeof seoScore === 'number' ? seoScore : parseFloat(seoScore) || 0;
    const max = typeof maxScore === 'number' ? maxScore : parseFloat(maxScore) || 1;

    if (max <= 0) return 0;

    return Math.max(0, Math.min(100, Math.round((earned / max) * 100)));
  } catch (e) {
    console.error('Error in word count:', e);
    return 0;
  }
};

/**
 * Get score label based on score value
 */
export const getScoreLabel = (score) => {
  if (score >= 80) return 'Excellent';
  if (score >= 60) return 'Good';
  if (score >= 40) return 'Average';
  return 'Poor';
};

/**
 * Calculate Domain Authority score
 * Formula: (DA * 0.7) + (PA * 0.3)
 */
export const calculateDomainAuthorityScore = (data) => {
  try {
    let dataList = [];
    
    if (Array.isArray(data)) {
      dataList = data;
    } else if (data && typeof data === 'object' && Array.isArray(data.data)) {
      dataList = data.data;
    } else if (data && typeof data === 'object') {
      dataList = [data];
    }
    
    if (dataList.length === 0) return 0;
    
    let domainAuthority = 0;
    let pageAuthority = 0;
    
    for (const item of dataList) {
      if (typeof item === 'object') {
        domainAuthority = item['Domain Authority Score'] || domainAuthority;
        pageAuthority = item['Page Authority Score'] || pageAuthority;
      }
    }
    
    const score = Math.round((domainAuthority * 0.7) + (pageAuthority * 0.3));
    return Math.max(0, Math.min(100, score));
  } catch (e) {
    console.error('Error calculating domain authority score:', e);
    return 0;
  }
};

/**
 * Calculate Technical Audit score
 * Percentage of passed/optimized checks
 */
export const calculateTechnicalAuditScore = (data) => {
  try {
    let analysis = {};
    
    if (data && typeof data === 'object') {
      // Handle various data structure formats
      if (data['Technical analysis']) {
        analysis = data['Technical analysis'];
      } else if (data.data && typeof data.data === 'object') {
        if (data.data['Technical analysis']) {
          analysis = data.data['Technical analysis'];
        } else if (data.data.data && typeof data.data.data === 'object') {
          analysis = data.data.data['Technical analysis'] || {};
        }
      }
    }
    
    const failed = Array.isArray(analysis.Failed) ? analysis.Failed : [];
    const passed = Array.isArray(analysis.Passed) ? analysis.Passed : [];
    
    const allItems = [...failed, ...passed];
    const unique = {};
    
    for (const item of allItems) {
      if (typeof item === 'object') {
        const key = String(item['element name'] || '').toLowerCase().trim();
        if (key) {
          unique[key] = item;
        }
      }
    }
    
    const values = Object.values(unique);
    if (values.length === 0) return 0;
    
    const passedCount = values.filter(
      item => String(item.status || '').toLowerCase().includes('optimized')
    ).length;
    
    return Math.round((passedCount / values.length) * 100);
  } catch (e) {
    console.error('Error calculating technical audit score:', e);
    return 0;
  }
};

/**
 * Calculate Website Traffic score
 * Based on organic traffic, paid traffic, and costs
 */
export const calculateWebsiteTrafficScore = (data) => {
  try {
    // Handle if data is wrapped in data.data structure
    let dataList = [];
    
    if (Array.isArray(data)) {
      dataList = data;
    } else if (data && typeof data === 'object' && Array.isArray(data.data)) {
      dataList = data.data;
    } else if (data && typeof data === 'object') {
      dataList = [data];
    }
    
    if (dataList.length === 0) return 0;
    
    const firstItem = dataList[0];
    if (!firstItem || typeof firstItem !== 'object') return 0;
    
    const metrics = firstItem.metrics || {};
    
    const organic = parseFloat(metrics.organic?.traffic || 0);
    const paid = parseFloat(metrics.paid?.traffic || 0);
    const cost = parseFloat(metrics.paid?.costs || 0);
    
    // Scoring logic (matches backend):
    // - Organic: up to 70 points based on traffic (50+ traffic = 70 points)
    // - Paid: 20 points if paid traffic exists
    // - Cost: 10 points if costs exist
    
    let organicScore = (organic / 50) * 70;
    organicScore = Math.min(organicScore, 70);
    
    const paidScore = paid > 0 ? 20 : 0;
    const costScore = cost > 0 ? 10 : 0;
    
    const totalScore = Math.round(organicScore + paidScore + costScore);
    return Math.max(0, Math.min(100, totalScore));
  } catch (e) {
    console.error('Error calculating website traffic score:', e);
    return 0;
  }
};

/**
 * Calculate Backlink Overview score
 * Mirrors backend DomainScoreCalculator.calculate_backlink_score
 */
export const calculateBacklinkOverviewScore = (data) => {
  try {
    let firstItem = null;

    if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
      firstItem = data[0];
    } else if (data && typeof data === 'object') {
      if (Array.isArray(data.data) && data.data.length > 0) {
        firstItem = data.data[0];
      } else if (data.data && Array.isArray(data.data.data) && data.data.data.length > 0) {
        firstItem = data.data.data[0];
      } else {
        firstItem = data;
      }
    }

    if (!firstItem || typeof firstItem !== 'object') return 0;

    const backlinks = parseFloat(firstItem['Backlinks total'] || 0) || 0;
    const domains = parseFloat(firstItem['Unique domains'] || 0) || 0;
    const da = parseFloat(firstItem['Domain Authority'] || 0) || 0;
    const spam = parseFloat(firstItem['Spam Score'] || 0) || 0;
    const broken = parseFloat(firstItem['Broken backlinks'] || 0) || 0;

    let score = 0;
    score += (Math.min(domains, 50) / 50) * 35;
    score += (Math.min(backlinks, 100) / 100) * 20;
    score += (Math.min(da, 40) / 40) * 20;
    score += (Math.min(10 - spam, 10) / 10) * 15;
    score += broken === 0 ? 10 : 4;

    return Math.max(0, Math.min(100, Math.round(score)));
  } catch (e) {
    console.error('Error calculating backlink overview score:', e);
    return 0;
  }
};

/**
 * Calculate Robots.txt score (Found => 100 else 0)
 * Mirrors backend DomainScoreCalculator.calculate_robots_txt_score
 */
export const calculateRobotsTxtScore = (data) => {
  try {
    let firstItem = null;

    if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
      firstItem = data[0];
    } else if (data && typeof data === 'object') {
      if (Array.isArray(data.data) && data.data.length > 0) {
        firstItem = data.data[0];
      } else if (data.data && Array.isArray(data.data.data) && data.data.data.length > 0) {
        firstItem = data.data.data[0];
      } else {
        firstItem = data;
      }
    }

    if (!firstItem || typeof firstItem !== 'object') return 0;

    const found = firstItem['Robots.txt file found'] || firstItem['Robots.txt Found'] || firstItem['robots.txt found'];
    return found === 'Found' || found === true ? 100 : 0;
  } catch (e) {
    console.error('Error calculating robots.txt score:', e);
    return 0;
  }
};

/**
 * Calculate SERP Data score
 * Percentage of keywords in top 10
 * Mirrors backend DomainScoreCalculator.calculate_serp_data_score
 */
export const calculateSerpDataScore = (data) => {
  try {
    let dataList = [];

    if (Array.isArray(data)) {
      dataList = data;
    } else if (data && typeof data === 'object') {
      if (Array.isArray(data.data)) {
        dataList = data.data;
      } else if (data.data && Array.isArray(data.data.data)) {
        dataList = data.data.data;
      }
    }

    if (!dataList || dataList.length === 0) return 0;

    const top10 = dataList.filter((item) => {
      if (!item || typeof item !== 'object') return false;
      const rank = item.rank_absolute ?? item['rank_absolute'] ?? item['Rank'] ?? item['rank'] ?? null;
      return Number.isFinite(Number(rank)) && Number(rank) <= 10;
    }).length;

    const score = Math.round((top10 / dataList.length) * 100);
    return Math.max(0, Math.min(100, score));
  } catch (e) {
    console.error('Error calculating SERP data score:', e);
    return 0;
  }
};

/**
 * Get score color based on score value
 */
export const getScoreColor = (score) => {
  if (score >= 80) return '#10b981'; // green
  if (score >= 60) return '#84cc16'; // light green
  if (score >= 40) return '#f97316'; // orange
  return '#ef4444'; // red
};

const extractScoreFromMetric = (metric) => {
  if (!metric || typeof metric !== 'object') return null;

  const score =
    metric.overall_score ??
    metric.score ??
    metric.data?.overall_score ??
    metric.data?.score ??
    metric.result?.overall_score ??
    metric.result?.score ??
    metric.data?.result?.overall_score ??
    metric.data?.result?.score;

  const num = typeof score === 'number' ? score : Number(score);
  return Number.isFinite(num) ? num : null;
};

/**
 * Calculate score for any endpoint
 */
const normalizeMetricPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return payload;

  // Common wrapper: { status, result, data }
  if (Object.prototype.hasOwnProperty.call(payload, 'data')) {
    const inner = payload.data;
    if (inner && typeof inner === 'object') {
      // Some tools wrap again in data.data
      if (Object.prototype.hasOwnProperty.call(inner, 'data')) {
        return inner.data;
      }
      return inner;
    }
  }

  return payload;
};

export const calculateScore = (endpoint, data) => {
  endpoint = (endpoint || '').toLowerCase().trim();
  const normalizedData = normalizeMetricPayload(data);
  
  const preview = JSON.stringify(normalizedData).substring(0, 100);
  console.log(`🔢 calculateScore('${endpoint}'): data type = ${typeof normalizedData}, value = ${preview}`);

  switch (endpoint) {
    // Title
    case 'title':
    case 'title_tag':
      return calculateTitleTagScore(normalizedData);

    // PWA
    case 'pwa':
    case 'pwa_audit':
      return calculatePwaAuditScore(normalizedData);

    // Keywords
    case 'keyword':
    case 'keyword_suggestions':
      return calculateKeywordSuggestionsScore(normalizedData);

    // Meta
    case 'meta':
    case 'meta_description':
      return calculateMetaDescriptionScore(normalizedData);

    // Headings
    case 'h1':
    case 'headings':
      return calculateHeadingsScore(normalizedData);

    // Readability
    case 'readability':
    case 'readability_score':
      return calculateReadabilityScore(normalizedData);

    // Content
    case 'seo_content_analysis':
      return calculateSeoContentAnalysisScore(normalizedData);

    // Others
    case 'canonical':
      return calculateCanonicalScore(normalizedData);

    case 'image_analysis':
      return calculateImageAnalysisScore(normalizedData);

    case 'helpful_content':
    case 'helpfull_content':
      return calculateHelpfulContentScore(normalizedData);

    case 'on_page_links':
    case 'on_page_links_summary':
    case 'internal-links':
    case 'internal_links':
      return calculateOnPageLinksScore(normalizedData);

    case 'keyword_density':
      console.log('[DEBUG] Keyword Density - normalizedData:', normalizedData);
      return calculateKeywordDensityScore(normalizedData);

    case 'word_count':
      return calculateWordCountScore(normalizedData);

    // Domain-level endpoints with custom calculation
    case 'website_traffic':
      return calculateWebsiteTrafficScore(normalizedData);
    
    case 'domain_authority':
      return calculateDomainAuthorityScore(normalizedData);
    
    case 'technical_audit':
      return calculateTechnicalAuditScore(normalizedData);
    
    // Domain-level endpoints (use generic score extraction)
    case 'backlink_overview':
      return calculateBacklinkOverviewScore(normalizedData);

    case 'serp_data':
      return calculateSerpDataScore(normalizedData);

    case 'robots_txt':
      return calculateRobotsTxtScore(normalizedData);

    default: {
      const fallback = extractScoreFromMetric(data);
      if (fallback !== null) {
        return fallback;
      }
      console.log(`❌ Unknown endpoint "${endpoint}"`);
      return 0;
    }
  }
};
